/* eslint-disable max-len */
import moment from 'moment-timezone';
import {
  AccountType,
  CreditPlan,
  DEFAULT_EMPTY_CONTENT,
  NumberOfEmployees,
  SURVEY_FILE_IMPORT_STATUS,
  SubscriptionStatus,
} from '../shared/Constants';
import ArrayUtil from './ArrayUtil';

export const importValidStyle = (status) =>
  status === SURVEY_FILE_IMPORT_STATUS.VALID ? 'fa fa-eye action-icon' : 'fa fa-eye action-icon disabled';

export const activeIconStyle = (status) => {
  switch (status) {
    case SURVEY_FILE_IMPORT_STATUS.ACTIVATIVED:
      return 'fa fa-check-square-o action-icon';
    case SURVEY_FILE_IMPORT_STATUS.VALID:
      return 'fa fa-square-o action-icon';
    default:
      return 'fa fa-square-o action-icon disabled';
  }
};

export const logIconStyle = (status) =>
  status === SURVEY_FILE_IMPORT_STATUS.FAILED ? 'fa fa-eye action-icon' : 'fa fa-eye action-icon disabled';

const DEV_VERSION = {
  last_commit_id: 'last commit id',
  log: 'last commit message',
  build_version: 'developing',
  application_name: 'App Name',
  build_date: 'release date',
};

export const getVersion = () => {
  const url = 'version.json';
  return fetch(url)
    .then((res) => res.json())
    .catch(() => DEV_VERSION);
};

export const getViewerDefaultAccount = (accountEdges) => {
  const account = ArrayUtil.isNotEmpty(accountEdges) ? accountEdges.slice().shift() : null;
  if (account) {
    return account.node;
  }

  return null;
};

export const getEntityListWithAccount = (account, field) => {
  const entityGraph = account ? account[field] : { [field]: {} };
  const processedData = entityGraph.edges ? entityGraph.edges.map((r) => r.node) : [];

  return processedData;
};

export const getNestedEntityList = (data, field) => {
  const entityGraph = data ? data[field] : { [field]: {} };
  const processedData = ArrayUtil.isNotEmpty(entityGraph.edges) ? entityGraph.edges.map((r) => r.node) : [];

  return processedData;
};

export const isOwner = (role) => {
  if (!role) {
    return false;
  }
  return role.toLowerCase() === 'owner' || role.toLowerCase() === 'nbm-owner';
};

export const isAdmin = (role) => {
  if (!role) {
    return false;
  }
  return role.toLowerCase() === 'admin';
};

export const isExternalSupport = (role) => {
  if (!role) {
    return false;
  }
  return role.toLowerCase() === 'external_support';
};

export const hasPermission = (scopes, permission) => {
  if (!scopes) {
    return false;
  }
  return scopes.includes(permission);
};

export const compare = (a, b) => {
  if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
  if (b.label.toLowerCase() > a.label.toLowerCase()) return -1;
  return 0;
};

export const isAccountOfNewBusinessModelType = (type) =>
  type === AccountType.GSC || type === AccountType.GP || type === AccountType.NBM_CLIENT;

export const getNumberOfEmployeesLabel = (numbersOfEmployeesOption) => {
  switch (numbersOfEmployeesOption) {
    case NumberOfEmployees._0_9:
      return '0 - 9';
    case NumberOfEmployees._10_24:
      return '10 - 24';
    case NumberOfEmployees._25_49:
      return '25 - 49';
    case NumberOfEmployees._50_74:
      return '50 - 74';
    case NumberOfEmployees._75_99:
      return '75 - 99';
    case NumberOfEmployees._100_249:
      return '100 - 249';
    case NumberOfEmployees._250_499:
      return '250 - 499';
    case NumberOfEmployees._500_999:
      return '500 - 999';
    case NumberOfEmployees._1000_2499:
      return '1000 - 2499';
    case NumberOfEmployees._2500_4999:
      return '2500 - 4999';
    case NumberOfEmployees._5000_7499:
      return '5000 - 7499';
    case NumberOfEmployees._7500_9999:
      return '7500 - 9999';
    case NumberOfEmployees._10000_14999:
      return '10000 - 14999';
    case NumberOfEmployees._15000_19999:
      return '15000 - 19999';
    case NumberOfEmployees._20000_24999:
      return '20000 - 24999';
    default:
      return DEFAULT_EMPTY_CONTENT;
  }
};

export const buildSubscriptionPeriodText = (pluralText, singularText, duration) => {
  const timeUnit = duration > 1 ? pluralText : singularText;

  return `${duration} ${timeUnit}`;
};

export const getFormattedDate = (date, formatDate) =>
  formatDate(moment(date), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });

export const getSubscriptionStatusText = (status, activeText, trialingText, pausedText) => {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return activeText;
    case SubscriptionStatus.TRIALING:
      return trialingText;
    case SubscriptionStatus.PAUSED:
      return pausedText;
    case SubscriptionStatus.PAST_DUE:
      return 'Passed due';
    case SubscriptionStatus.UNPAID:
      return 'Unpaid';
    case SubscriptionStatus.CANCELED:
      return 'Canceled';
    default:
      throw new Error(`Unknown status: ${status}`);
  }
};

export const getBusinessCenterAccountStatusDisplayName = (status, activeText, pausedText, awaitingSubscriptionText) => {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
    case SubscriptionStatus.TRIALING:
      return activeText;
    case SubscriptionStatus.PAUSED:
      return pausedText;
    case SubscriptionStatus.PAST_DUE:
      return 'Passed due';
    case SubscriptionStatus.UNPAID:
      return 'Unpaid';
    case SubscriptionStatus.CANCELED:
      return 'Canceled';
    case null:
      return awaitingSubscriptionText;
    default:
      throw new Error(`Unknown status: ${status}`);
  }
};

export const getFormattedSubscriptionCost = (subscriptionCost) => {
  if (!subscriptionCost) return '';

  return `$${subscriptionCost.toLocaleString()}`;
};

export const getCreditPlanDisplayName = (plan, standardDisplayName, unlimitedDisplayName, noLimitDisplayName) => {
  switch (plan) {
    case CreditPlan.STANDARD:
      return standardDisplayName;
    case CreditPlan.UNLIMITED:
      return unlimitedDisplayName;
    case CreditPlan.NO_LIMIT:
      return noLimitDisplayName;
    default:
      throw new Error(`Unknown credit plan: ${plan}`);
  }
};

export const buildVatIdExampleText = (selectedTaxId) => {
  let vatIdPlaceholderExample;

  if (!!selectedTaxId) {
    selectedTaxId.variants.forEach((variant) => {
      const variantIndex = selectedTaxId.variants.indexOf(variant);
      const example = selectedTaxId.variants[variantIndex].example;

      if (variantIndex === 0) {
        vatIdPlaceholderExample = example;

        return;
      } else {
        vatIdPlaceholderExample = `${vatIdPlaceholderExample} / ${example}`;

        return;
      }
    });
  }

  return vatIdPlaceholderExample;
};
